/**
 * OrgMembersPage - 조직원관리 페이지
 * V135: 팀관리 → 조직관리 메뉴 개편
 * V137: 조직관리 스코프 표시
 * V137.1: 실제 직원 목록 표시 (스코프 기반 필터링)
 * V137.4: 팀장(TL) 역할 배정/해제 기능 추가
 * V138: 역할 컬럼 개편 (TE/TL/Manager 표시, TE↔TL 토글)
 * V138.1: 역할 컬럼 분리 - "역할"(표시 전용) + "역할변경"(액션 버튼)
 * V139: 역할변경 UX 개선 - [역할 변경] 버튼 + confirm 모달
 * V140: 역할변경 버튼 노출 조건 수정 - TE/TL인 경우에만 표시
 * V141: assignedRoleIds 우선 사용 - auth_roleAssignments 기반 역할 정확 표시
 *       Manager/HR/시스템어드민 등은 역할변경 대상 아님
 * V144: users.roleIds fallback 완전 제거 - assignedRoleIds만 사용
 * V146: 역할 표시/스코프/버튼 노출 조건 완전 정비
 *   - 역할 라벨: auth_roleAssignments 기반 (Manager/Exec/HR/시스템어드민 정확 표시)
 *   - 스코프: 백엔드에서 확정 (role_manager/exec → SUBTREE)
 *   - 버튼: TE/TL에만 노출, 다른 역할(role_manager/exec/hr/system_admin)은 "-"
 * V65: 조직관리 3페이지 공용 정렬 적용 (직책→직위→입사일→이름)
 *
 * 조직관리 스코프 내의 조직원 목록을 표시합니다.
 */

import React, { useMemo, useState, useCallback } from 'react';
import { Users, Shield, AlertCircle, Search, RefreshCw, X } from 'lucide-react';
import { useOrgManagementScope } from '../../shared/hooks/useOrgManagementScope';
import { useOrgManagementPolicy } from '../../shared/hooks/useOrgManagementPolicy';
import { useEmployees } from '../../shared/hooks/useEmployees';
import { useTeamLeaderAssignment, hasTeamLeaderRole } from '../../shared/hooks/useTeamLeaderAssignment';
import { useAuthorization } from '../../shared/hooks/useAuthorization';
// V65: authContext 제거 (역할 기반 정렬 제거됨)
import { useJobTitles } from '../../shared/hooks/useJobTitles';
import { usePositionLevelMap } from '../../shared/hooks/usePositionLevelMap';
import { useTeamAttendanceStatus } from '../../shared/hooks/useTeamAttendanceStatus';
import { Employee } from '../../shared/types/employee';
import { JOB_TITLE_LEVEL_ORDER, JobTitleLevel } from '../../shared/types/orgManagementPolicy';
import { ADMIN_LIKE_ROLES } from '../../types/role';
// V5.17: 조직원 프로필 카드 모달
import { OrgMemberProfileModal } from './components/OrgMemberProfileModal';
import { ModalOverlay } from '../../shared/components/ModalOverlay';
// V65: 공용 정렬 유틸
import {
  compareOrgMember,
  buildJobTitleMaps,
  buildPositionLevelMaps,
  OrgMemberSortMaps,
} from '../../shared/utils/sortOrgMembers';
// V68: 고용형태별 시작일/만료일 분기 헬퍼 (DISPATCH/CONTRACT/INTERN/REGULAR)
import { getCurrentEmploymentEndDate, getCurrentEmploymentStartDate } from '../../shared/utils/employmentDates';

/**
 * V142: 역할 라벨 매핑
 * - org_manager → role_manager (조직장)
 * - role_exec 추가 (임원)
 * V29.2: role_admin_kr, role_admin_cn 추가
 */
const ROLE_LABELS: Record<string, string> = {
  system_admin: '시스템어드민',
  role_admin_kr: '관리자(KR)',
  role_admin_cn: '관리자(CN)',
  role_hr: '인사팀',
  role_exec: '임원',
  role_manager: '조직장',
  role_tl: 'TL',
};

/**
 * V147.2: 역할별 뱃지 색상 매핑
 * - role_exec: 보라색
 * - role_manager: 노란색
 * - role_tl: 초록색
 * - role_hr: 연한 파란색
 * - 기타 (TE, system_admin 등): 기본 스타일 (회색)
 * V29.2: role_admin_kr, role_admin_cn 추가 (빨간색 계열 - 관리자)
 */
const ROLE_BADGE_COLORS: Record<string, { color: string; background: string; border: string }> = {
  role_exec: { color: '#7c3aed', background: '#f3e8ff', border: '#c4b5fd' },      // 보라색
  role_manager: { color: '#ca8a04', background: '#fef9c3', border: '#fde047' },  // 노란색
  role_tl: { color: '#16a34a', background: '#dcfce7', border: '#86efac' },       // 초록색
  role_hr: { color: '#0284c7', background: '#e0f2fe', border: '#7dd3fc' },       // 연한 파란색
  // V29.2: 관리자급 역할 (빨간색 계열)
  system_admin: { color: '#dc2626', background: '#fef2f2', border: '#fca5a5' },  // 빨간색
  role_admin_kr: { color: '#dc2626', background: '#fef2f2', border: '#fca5a5' }, // 빨간색
  role_admin_cn: { color: '#dc2626', background: '#fef2f2', border: '#fca5a5' }, // 빨간색
};

/**
 * V144: 역할 표시용 ID 배열 가져오기
 * - assignedRoleIds만 사용 (auth_roleAssignments 기반)
 * - users.roleIds fallback 완전 제거
 */
function getEffectiveRoleIds(employee: { assignedRoleIds?: string[] }): string[] {
  return employee.assignedRoleIds ?? [];
}

/**
 * V138.1: 역할 표시 문자열 생성 (복수 역할 지원)
 * V141: assignedRoleIds 우선 사용
 * - 매핑되는 역할들을 "·"로 연결
 * - 매핑되는 역할이 없으면 "TE"
 */
function getRoleDisplayText(roleIds: string[] | undefined): string {
  if (!roleIds || roleIds.length === 0) return 'TE';

  const displayRoles: string[] = [];
  for (const roleId of roleIds) {
    if (ROLE_LABELS[roleId]) {
      displayRoles.push(ROLE_LABELS[roleId]);
    }
  }

  return displayRoles.length > 0 ? displayRoles.join(' · ') : 'TE';
}

/**
 * V142: 조직장(Manager) 여부 확인
 * - org_manager → role_manager로 통일
 */
function isManager(roleIds: string[] | undefined): boolean {
  return roleIds?.includes('role_manager') ?? false;
}

/**
 * V138.1: TL 여부 확인
 */
function isTL(roleIds: string[] | undefined): boolean {
  return roleIds?.includes('role_tl') ?? false;
}

/**
 * V142: TE 또는 TL인지 확인 (역할변경 대상 여부)
 * - TE: roleIds에 주요 역할이 없는 경우
 * - TL: roleIds에 role_tl만 포함 (다른 주요 역할 없음)
 * - 임원/조직장/HR/시스템어드민 등: 역할변경 대상 아님
 *
 * V142: org_manager → role_manager, role_exec 추가
 * V29.2: system_admin 하드코딩 제거 → ADMIN_LIKE_ROLES 사용 (role_admin_kr/cn 포함)
 */
function isTeOrTlOnly(roleIds: string[] | undefined): boolean {
  if (!roleIds || roleIds.length === 0) return true; // TE

  // 주요 역할 체크 (TL 제외)
  const hasExec = roleIds.includes('role_exec');       // V142: 임원
  const hasManager = roleIds.includes('role_manager'); // V142: 조직장
  const hasHr = roleIds.includes('role_hr');
  // V29.2: system_admin 하드코딩 제거 → ADMIN_LIKE_ROLES 사용
  const hasAdminLike = ADMIN_LIKE_ROLES.some(roleId => roleIds.includes(roleId));

  // role_exec, role_manager, role_hr, 관리자급(system_admin, role_admin_kr, role_admin_cn)이 있으면 역할변경 대상 아님
  if (hasExec || hasManager || hasHr || hasAdminLike) return false;

  // role_tl만 있거나, 아무 역할도 없으면 TE 또는 TL → 역할변경 가능
  return true;
}

/**
 * V139: 직책 레벨이 TEAM_LEADER 이상인지 확인
 */
function isTeamLeaderOrAbove(level: JobTitleLevel | undefined): boolean {
  if (!level) return false;
  const order = JOB_TITLE_LEVEL_ORDER[level];
  return order <= 4; // TEAM_LEADER=4 이하
}

/**
 * 스코프 모드 → 한글 라벨
 */
const SCOPE_MODE_LABELS = {
  SELF: '본인 조직만',
  SUBTREE: '하위 조직 포함',
  ALL: '전체 조직',
};

/**
 * 상태 라벨 변환
 */
function getStatusLabel(status: string | undefined): string {
  switch (status) {
    case 'active':
      return '재직';
    case 'on_leave':
      return '휴직';
    case 'terminated':
      return '퇴직';
    default:
      return '-';
  }
}

/**
 * 상태별 스타일
 */
function getStatusStyle(status: string | undefined): React.CSSProperties {
  switch (status) {
    case 'active':
      return { color: 'var(--color-green-700)', background: 'var(--color-green-50)' };
    case 'on_leave':
      return { color: 'var(--color-yellow-700)', background: 'var(--color-yellow-50)' };
    case 'terminated':
      return { color: 'var(--color-red-700)', background: 'var(--color-red-50)' };
    default:
      return { color: 'var(--color-gray-600)', background: 'var(--color-gray-100)' };
  }
}

export function OrgMembersPage() {
  const {
    isLoading: scopeLoading,
    error: scopeError,
    scopeOrgUnitIds,
    scopeMode,
    requester,
  } = useOrgManagementScope();

  // V137.8: 스코프가 준비되었는지 확인
  // scopeLoading=false이고 scopeOrgUnitIds.length > 0이면 준비 완료
  const isScopeReady = !scopeLoading && scopeOrgUnitIds.length > 0;

  // V137.8: 스코프 기반 직원 목록 조회
  // 스코프가 준비되기 전에는 호출하지 않음 (HR 권한 에러 방지)
  const {
    employees,
    isLoading: employeesLoading,
    error: employeesError,
  } = useEmployees(
    isScopeReady
      ? {
          orgUnitIds: scopeOrgUnitIds,
          scopeMode: scopeMode || undefined,
          status: 'active_or_on_leave', // V137.4: 재직 + 휴직 (퇴사자 제외)
        }
      : {} // 스코프 미준비 시 빈 필터 → useEmployees 내부에서 호출 스킵
  );

  // V137.4: TL 역할 배정/해제
  const { assignTeamLeader, removeTeamLeader, isLoading: tlLoading } = useTeamLeaderAssignment();
  const { hasRole, user } = useAuthorization();

  // V65: authContext 제거 (역할 기반 정렬 제거됨)

  // V139: 직책 레벨 정보 가져오기
  const { userJobTitleLevel, isSystemAdmin } = useOrgManagementPolicy();

  // V147: 정렬용 마스터 데이터 조회
  // V148.2: 마스터 데이터 로딩 완료 시 resolveJobTitleOrder가 재생성되어 sortedEmployees 재정렬됨
  const { jobTitles } = useJobTitles({ status: 'active' });
  const { positionLevelMap } = usePositionLevelMap();

  // V74.4: 근태 상태 조회 (CF 경유)
  const employeeIds = useMemo(() => employees.map((e) => e.id), [employees]);
  const { statusMap: attendanceStatusMap, error: attendanceError } = useTeamAttendanceStatus(employeeIds);

  // V142: TL 배정 권한 확인 (정책 A 조건과 동일)
  // userType='admin' OR system_admin OR role_hr OR role_manager OR role_exec OR jobTitleLevel >= TEAM_LEADER
  const canAssignTL = useMemo(() => {
    const isAdminUser = (user?.profile as any)?.userType === 'admin';
    if (isAdminUser) return true;
    if (isSystemAdmin) return true;
    if (hasRole('role_hr')) return true;
    if (hasRole('role_manager')) return true;   // V142: org_manager → role_manager
    if (hasRole('role_exec')) return true;      // V142: 임원 추가
    if (isTeamLeaderOrAbove(userJobTitleLevel)) return true;
    return false;
  }, [hasRole, user, isSystemAdmin, userJobTitleLevel]);

  // V58: 탭 상태
  const [activeTab, setActiveTab] = useState<'members'>('members');

  // 검색어 상태
  const [searchText, setSearchText] = useState('');

  // V5.17: 프로필 카드 모달 상태
  const [profileModalUserId, setProfileModalUserId] = useState<string | null>(null);

  // V139: 역할 변경 confirm 모달 상태
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    employee: Employee | null;
    targetRole: 'TL' | 'TE';
  }>({ isOpen: false, employee: null, targetRole: 'TL' });

  // V139: 역할 변경 중인 직원 ID (UI 피드백용)
  const [roleChangingId, setRoleChangingId] = useState<string | null>(null);

  // V141: [역할 변경] 버튼 클릭 → 모달 열기
  const handleOpenRoleChangeModal = useCallback((employee: Employee) => {
    // assignedRoleIds 우선 사용
    const effectiveRoles = getEffectiveRoleIds(employee);

    // TE/TL이 아닌 경우 변경 불가
    if (!isTeOrTlOnly(effectiveRoles)) {
      return;
    }

    const employeeIsTL = isTL(effectiveRoles);
    setConfirmModal({
      isOpen: true,
      employee,
      targetRole: employeeIsTL ? 'TE' : 'TL',
    });
  }, []);

  // V139: 모달 닫기
  const handleCloseModal = useCallback(() => {
    setConfirmModal({ isOpen: false, employee: null, targetRole: 'TL' });
  }, []);

  // V139: 역할 변경 확인 (모달에서 [확인] 클릭 시)
  const handleConfirmRoleChange = useCallback(async () => {
    const { employee, targetRole } = confirmModal;
    if (!employee || tlLoading || roleChangingId) return;

    setRoleChangingId(employee.id);
    handleCloseModal();

    try {
      if (targetRole === 'TE') {
        // TL → TE (해제)
        const result = await removeTeamLeader(employee.id);
        if (result.success) {
          alert(`${employee.nameKo || '직원'}님을 TE로 변경했습니다.`);
          window.location.reload();
        } else {
          alert(`오류: ${result.message}`);
        }
      } else {
        // TE → TL (배정)
        const result = await assignTeamLeader(employee.id);
        if (result.success) {
          alert(`${employee.nameKo || '직원'}님을 TL로 변경했습니다.`);
          window.location.reload();
        } else {
          alert(`오류: ${result.message}`);
        }
      }
    } catch (error: any) {
      const errorMessage = error?.message || '역할 변경 중 오류가 발생했습니다.';
      alert(errorMessage);
    } finally {
      setRoleChangingId(null);
    }
  }, [confirmModal, tlLoading, roleChangingId, assignTeamLeader, removeTeamLeader, handleCloseModal]);

  // ============================================
  // V65: 공용 정렬 유틸 적용 (sortOrgMembers.ts)
  // 정렬 우선순위: 직책→직위→입사일→이름
  // ============================================

  // A) V148.5: titleId → name 매핑 (직책 컬럼 표시용) - 유지
  const titleIdToNameMap = useMemo(() => {
    const m: Record<string, string> = {};
    jobTitles.forEach(jt => {
      if (jt.id && jt.name) m[jt.id] = jt.name;
    });
    return m;
  }, [jobTitles]);

  // B) V65: 공용 정렬 맵 생성
  const sortMaps: OrgMemberSortMaps = useMemo(() => {
    const jobTitleMaps = buildJobTitleMaps(jobTitles);
    const positionLevelMaps = buildPositionLevelMaps(
      Object.values(positionLevelMap)
    );
    return {
      ...jobTitleMaps,
      ...positionLevelMaps,
    };
  }, [jobTitles, positionLevelMap]);

  // V65: 공용 정렬 적용
  // 1순위: 직책순 (높은 사람 먼저) - order 오름차순
  // 2순위: 직위순 (높은 사람 먼저) - rankWeight 내림차순
  // 3순위: 입사일순 (오래된 순) - 오름차순
  // 4순위: 이름 가나다순 - 오름차순
  const sortedEmployees = useMemo(() => {
    return [...employees].sort((a, b) => compareOrgMember(a, b, sortMaps));
  }, [employees, sortMaps]);

  // 검색 필터링 (프론트엔드)
  const filteredEmployees = useMemo(() => {
    if (!searchText.trim()) return sortedEmployees;

    const search = searchText.toLowerCase();
    return sortedEmployees.filter((emp) =>
      emp.nameKo?.toLowerCase().includes(search) ||
      emp.email?.toLowerCase().includes(search) ||
      emp.employeeNumber?.toLowerCase().includes(search)
    );
  }, [sortedEmployees, searchText]);

  // 로딩 상태
  // V148.2: 마스터 데이터 로딩은 포함하지 않음 (목록은 먼저 표시, 정렬은 나중에 적용)
  const isLoading = scopeLoading || (scopeOrgUnitIds.length > 0 && employeesLoading);

  // 에러 상태
  const error = scopeError || employeesError || attendanceError;

  // 테이블 컬럼 정의
  // V138.1: "역할"(표시 전용) + "역할변경"(액션 버튼) 분리
  const columns = useMemo(() => {
    const baseColumns = [
      { key: 'nameKo', label: '이름', width: '120px' },
      { key: 'email', label: '이메일', width: '180px' },
      { key: 'orgUnitName', label: '소속', width: '120px' },
      { key: 'jobTitle', label: '직책', width: '80px' },
      { key: 'positionYears', label: '직위', width: '100px' },
      { key: 'currentEmploymentType', label: '고용형태', width: '80px' },
      { key: 'firstHireDate', label: '최초입사일', width: '100px' },
      { key: 'currentEmploymentStartDate', label: '계약시작일', width: '100px' }, // V67
      { key: 'contractEndDate', label: '계약만료일', width: '100px' }, // V67
      { key: 'status', label: '상태', width: '70px' },
      { key: 'attendanceStatus', label: '근태 상태', width: '120px' }, // V74: 오늘 기준 근태 상태
      { key: 'role', label: '역할', width: '140px' }, // V138.1: 표시 전용 (복수 역할)
    ];

    // V138.1: 역할변경 권한이 있는 경우에만 "역할변경" 컬럼 추가
    if (canAssignTL) {
      baseColumns.push({ key: 'roleChange', label: '역할변경', width: '100px' });
    }

    return baseColumns;
  }, [canAssignTL]);

  // V5.17: 이름 클릭 핸들러
  const handleNameClick = useCallback((employeeId: string) => {
    setProfileModalUserId(employeeId);
  }, []);

  // 셀 렌더링
  const renderCell = (employee: Employee, columnKey: string): React.ReactNode => {
    switch (columnKey) {
      case 'nameKo':
        // V5.17: 이름 클릭 시 프로필 카드 모달 오픈
        return (
          <span
            style={{
              cursor: 'pointer',
              color: 'var(--color-gray-800)',
              textDecoration: 'underline',
              textDecorationColor: 'transparent',
              transition: 'text-decoration-color 0.2s',
            }}
            onMouseEnter={(e) => e.currentTarget.style.textDecorationColor = 'var(--color-gray-800)'}
            onMouseLeave={(e) => e.currentTarget.style.textDecorationColor = 'transparent'}
            onClick={() => handleNameClick(employee.id)}
          >
            {employee.nameKo || '-'}
          </span>
        );
      case 'email':
        return employee.email || '-';
      case 'project':
        return '-'; // 프로젝트 필드 미구현
      case 'orgUnitName':
        return employee.orgUnitName || '-';
      case 'jobTitle':
        // V148.7: titleId 기반 표시 (SSoT: jobTitles)
        return (employee.titleId && titleIdToNameMap[employee.titleId]) || employee.jobTitle || '-';
      case 'positionYears':
        // V137.4: 연차 계산 제거, 직위명만 표시
        return employee.positionTitle || '-';
      case 'currentEmploymentType':
        return employee.currentEmploymentType || '-';
      case 'firstHireDate':
        return employee.firstHireDate || '-';
      case 'currentEmploymentStartDate':
        // V68: 고용형태별 시작일 분기 (DISPATCH/CONTRACT/INTERN). REGULAR 는 계약 개념 아님 → '-'.
        return getCurrentEmploymentStartDate(employee);
      case 'contractEndDate':
        // V68: 고용형태별 만료일 분기 (DISPATCH/CONTRACT/INTERN). REGULAR 는 만료일 없음.
        return getCurrentEmploymentEndDate(employee);
      case 'status':
        const statusStyle = getStatusStyle(employee.status);
        return (
          <span
            style={{
              padding: '2px 8px',
              borderRadius: '4px',
              fontSize: '12px',
              fontWeight: '500',
              ...statusStyle,
            }}
          >
            {getStatusLabel(employee.status)}
          </span>
        );
      case 'attendanceStatus': {
        // V74: 오늘 근태 상태 표시
        const statusInfo = attendanceStatusMap.get(employee.id);
        const label = statusInfo?.label || '-';
        const attColor = statusInfo?.color || '#9ca3af';
        const attBg = statusInfo?.background || '#f3f4f6';
        return (
          <span
            style={{
              padding: '2px 8px',
              borderRadius: '4px',
              fontSize: '12px',
              fontWeight: '500',
              color: attColor,
              background: attBg,
              whiteSpace: 'nowrap',
            }}
          >
            {label}
          </span>
        );
      }
      case 'role':
        // V147.2 FIX: 역할 컬럼 - 다중 뱃지 표시 + 색상 적용
        // TE 미표시 버그 수정: null/undefined/빈배열 모두 TE로 처리
        const effectiveRoleIds = getEffectiveRoleIds(employee) ?? [];

        // 유효한 역할만 필터 (빈 문자열, null, undefined 제거)
        const validRoleIds = effectiveRoleIds.filter((id) => id && ROLE_LABELS[id]);

        // 역할이 없으면 TE 뱃지 (기본 스타일)
        if (validRoleIds.length === 0) {
          return (
            <div style={{ display: 'flex', justifyContent: 'center', gap: '4px', flexWrap: 'wrap' }}>
              <span
                style={{
                  padding: '2px 8px',
                  borderRadius: '4px',
                  fontSize: '11px',
                  fontWeight: '600',
                  color: 'var(--color-gray-700)',
                  background: 'var(--color-gray-100)',
                  border: '1px solid var(--color-gray-200)',
                }}
              >
                TE
              </span>
            </div>
          );
        }

        // 유효한 역할을 개별 뱃지로 표시
        return (
          <div style={{ display: 'flex', justifyContent: 'center', gap: '4px', flexWrap: 'wrap' }}>
            {validRoleIds.map((roleId) => {
              const label = ROLE_LABELS[roleId];
              const colorStyle = ROLE_BADGE_COLORS[roleId] || {
                color: 'var(--color-gray-700)',
                background: 'var(--color-gray-100)',
                border: 'var(--color-gray-200)',
              };

              return (
                <span
                  key={roleId}
                  style={{
                    padding: '2px 8px',
                    borderRadius: '4px',
                    fontSize: '11px',
                    fontWeight: '600',
                    color: colorStyle.color,
                    background: colorStyle.background,
                    border: `1px solid ${colorStyle.border}`,
                  }}
                >
                  {label}
                </span>
              );
            })}
          </div>
        );

      case 'roleChange':
        // V141: 역할변경 컬럼 - assignedRoleIds 기반으로 TE/TL 여부 판정
        // Manager/HR/시스템어드민 등은 "-" 표시
        const isChanging = roleChangingId === employee.id;
        const effectiveRolesForChange = getEffectiveRoleIds(employee);
        const canChangeRole = isTeOrTlOnly(effectiveRolesForChange);

        // TE/TL이 아닌 경우 (Manager, HR, 시스템어드민 등) → 변경 불가
        if (!canChangeRole) {
          return (
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <span
                style={{
                  padding: '2px 8px',
                  borderRadius: '4px',
                  fontSize: '11px',
                  fontWeight: '500',
                  color: 'var(--color-gray-400)',
                  background: 'var(--color-gray-50)',
                }}
              >
                -
              </span>
            </div>
          );
        }

        // TE 또는 TL → [역할 변경] 버튼 표시
        return (
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <button
              onClick={() => handleOpenRoleChangeModal(employee)}
              disabled={isChanging || tlLoading}
              title="역할 변경"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '4px 12px',
                borderRadius: '4px',
                border: '1px solid var(--color-primary)',
                cursor: isChanging || tlLoading ? 'not-allowed' : 'pointer',
                fontSize: '12px',
                fontWeight: '500',
                opacity: isChanging || tlLoading ? 0.5 : 1,
                background: 'white',
                color: 'var(--color-primary)',
              }}
            >
              {isChanging ? (
                <RefreshCw size={12} style={{ animation: 'spin 1s linear infinite' }} />
              ) : (
                '역할 변경'
              )}
            </button>
          </div>
        );
      default:
        return '-';
    }
  };

  return (
    <div style={{ padding: '24px' }}>
      <h1
        style={{
          fontSize: '24px',
          fontWeight: 'bold',
          marginBottom: '8px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
        }}
      >
        <Users size={24} />
        조직장 대시보드
      </h1>

      {/* V67: 페이지 사용 안내 (보조 설명, CSS variable 로 라이트/다크 모드 대응) */}
      <p
        style={{
          margin: '0 0 16px 0',
          fontSize: '13px',
          lineHeight: 1.6,
          color: 'var(--color-gray-600)',
        }}
      >
        조직원 이름을 클릭하면 직원 상세 정보를 확인할 수 있습니다.
        <br />
        역할 변경에서 직원을 TL로 지정하면 MM 등록 페이지 접근 및 작성 권한이 부여됩니다.
      </p>

      {/* V137: 스코프 요약 카드 (간소화) */}
      <div
        style={{
          padding: '16px 20px',
          background: 'white',
          borderRadius: '8px',
          border: '1px solid var(--color-gray-200)',
          marginBottom: '16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '12px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Shield size={16} style={{ color: 'var(--color-gray-500)' }} />
            <span style={{ fontSize: '13px', color: 'var(--color-gray-600)' }}>
              스코프:
            </span>
            <span
              style={{
                fontSize: '13px',
                fontWeight: '600',
                color:
                  scopeMode === 'ALL'
                    ? 'var(--color-primary)'
                    : 'var(--color-gray-800)',
              }}
            >
              {scopeMode ? SCOPE_MODE_LABELS[scopeMode] : '-'}
            </span>
          </div>

          {requester?.orgUnitName && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ fontSize: '13px', color: 'var(--color-gray-600)' }}>
                |
              </span>
              <span style={{ fontSize: '13px', color: 'var(--color-gray-600)' }}>
                소속:
              </span>
              <span style={{ fontSize: '13px', fontWeight: '500' }}>
                {requester.orgUnitName}
              </span>
            </div>
          )}

          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ fontSize: '13px', color: 'var(--color-gray-600)' }}>
              |
            </span>
            <span style={{ fontSize: '13px', color: 'var(--color-gray-600)' }}>
              조회 가능 조직:
            </span>
            <span
              style={{
                fontSize: '13px',
                fontWeight: '600',
                color: 'var(--color-green-700)',
              }}
            >
              {scopeOrgUnitIds.length}개
            </span>
          </div>
        </div>

        {/* 검색 입력 */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            background: 'var(--color-gray-50)',
            borderRadius: '6px',
            padding: '6px 12px',
            border: '1px solid var(--color-gray-200)',
          }}
        >
          <Search size={16} style={{ color: 'var(--color-gray-400)' }} />
          <input
            type="text"
            placeholder="이름, 이메일, 사번 검색"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{
              border: 'none',
              background: 'transparent',
              outline: 'none',
              fontSize: '13px',
              width: '180px',
            }}
          />
        </div>
      </div>

      {/* 조직원 탭 내용 */}
      {activeTab === 'members' && <>

      {/* 에러 표시 */}
      {error && (
        <div
          style={{
            padding: '16px',
            background: 'var(--color-red-50)',
            borderRadius: '6px',
            color: 'var(--color-red-700)',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '16px',
          }}
        >
          <AlertCircle size={18} />
          오류: {error.message}
        </div>
      )}

      {/* 스코프가 없으면 빈 목록 표시 */}
      {!scopeLoading && scopeOrgUnitIds.length === 0 && !error && (
        <div
          style={{
            padding: '40px',
            background: 'var(--color-gray-50)',
            borderRadius: '8px',
            textAlign: 'center',
            color: 'var(--color-gray-500)',
          }}
        >
          <AlertCircle size={32} style={{ marginBottom: '12px', opacity: 0.5 }} />
          <p style={{ fontSize: '14px' }}>
            조회 가능한 조직이 없습니다.
          </p>
        </div>
      )}

      {/* 로딩 표시 */}
      {isLoading && (
        <div
          style={{
            padding: '40px',
            textAlign: 'center',
            color: 'var(--color-gray-500)',
          }}
        >
          직원 목록 조회 중...
        </div>
      )}

      {/* 직원 목록 테이블 */}
      {!isLoading && !error && scopeOrgUnitIds.length > 0 && (
        <div
          style={{
            background: 'white',
            borderRadius: '8px',
            border: '1px solid var(--color-gray-200)',
            overflow: 'hidden',
          }}
        >
          {/* 결과 수 표시 */}
          <div
            style={{
              padding: '12px 16px',
              borderBottom: '1px solid var(--color-gray-200)',
              background: 'var(--color-gray-50)',
              fontSize: '13px',
              color: 'var(--color-gray-600)',
            }}
          >
            총{' '}
            <span style={{ fontWeight: '600', color: 'var(--color-gray-800)' }}>
              {filteredEmployees.length}
            </span>
            명
            {searchText && ` (검색: "${searchText}")`}
          </div>

          {/* 테이블 */}
          <div style={{ overflowX: 'auto' }}>
            <table
              style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: '13px',
              }}
            >
              <thead>
                <tr style={{ background: 'var(--color-gray-50)' }}>
                  {columns.map((col) => (
                    <th
                      key={col.key}
                      style={{
                        padding: '12px 16px',
                        textAlign: 'center',
                        fontWeight: '600',
                        color: 'var(--color-gray-700)',
                        borderBottom: '1px solid var(--color-gray-200)',
                        whiteSpace: 'nowrap',
                        width: col.width,
                      }}
                    >
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredEmployees.length === 0 ? (
                  <tr>
                    <td
                      colSpan={columns.length}
                      style={{
                        padding: '40px',
                        textAlign: 'center',
                        color: 'var(--color-gray-500)',
                      }}
                    >
                      {/* V137.7: 빈 상태 사유 구분 */}
                      {searchText
                        ? '검색 결과가 없습니다.'
                        : scopeOrgUnitIds.length === 0
                        ? '스코프 0개: 조회 가능한 조직이 없습니다.'
                        : employees.length === 0
                        ? `직원 0명: 스코프 내(${scopeOrgUnitIds.length}개 조직) 직원이 없습니다.`
                        : '조회된 조직원이 없습니다.'}
                    </td>
                  </tr>
                ) : (
                  filteredEmployees.map((employee) => (
                    <tr
                      key={employee.id}
                      style={{
                        borderBottom: '1px solid var(--color-gray-100)',
                      }}
                    >
                      {columns.map((col) => (
                        <td
                          key={col.key}
                          style={{
                            padding: '12px 16px',
                            textAlign: 'center',
                            color: 'var(--color-gray-800)',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {renderCell(employee, col.key)}
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      </>}
      {/* END 조직원 탭 내용 */}

      {/* V139: 역할 변경 confirm 모달 */}
      {confirmModal.isOpen && confirmModal.employee && (
        <ModalOverlay
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000,
          }}
          onClose={handleCloseModal}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '12px',
              padding: '24px',
              minWidth: '320px',
              maxWidth: '400px',
              boxShadow: '0 4px 24px rgba(0, 0, 0, 0.15)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* 모달 헤더 */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '16px',
              }}
            >
              <h3
                style={{
                  fontSize: '18px',
                  fontWeight: '600',
                  color: 'var(--color-gray-800)',
                  margin: 0,
                }}
              >
                역할 변경 확인
              </h3>
              <button
                onClick={handleCloseModal}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'var(--color-gray-400)',
                }}
              >
                <X size={20} />
              </button>
            </div>

            {/* 모달 내용 */}
            <div style={{ marginBottom: '24px' }}>
              <p
                style={{
                  fontSize: '14px',
                  color: 'var(--color-gray-600)',
                  margin: 0,
                  lineHeight: '1.6',
                }}
              >
                <strong style={{ color: 'var(--color-gray-800)' }}>
                  {confirmModal.employee.nameKo || '직원'}
                </strong>
                님의 역할을{' '}
                <strong
                  style={{
                    color:
                      confirmModal.targetRole === 'TL'
                        ? 'var(--color-blue-600)'
                        : 'var(--color-gray-600)',
                  }}
                >
                  {confirmModal.targetRole}
                </strong>
                로 변경하시겠습니까?
              </p>

              {/* 현재 → 변경 후 표시 */}
              <div
                style={{
                  marginTop: '16px',
                  padding: '12px 16px',
                  background: 'var(--color-gray-50)',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '12px',
                }}
              >
                <span
                  style={{
                    padding: '4px 12px',
                    borderRadius: '4px',
                    fontSize: '13px',
                    fontWeight: '600',
                    color:
                      confirmModal.targetRole === 'TL'
                        ? 'var(--color-gray-600)'
                        : 'var(--color-blue-700)',
                    background:
                      confirmModal.targetRole === 'TL'
                        ? 'var(--color-gray-100)'
                        : 'var(--color-blue-50)',
                    border:
                      confirmModal.targetRole === 'TL'
                        ? '1px solid var(--color-gray-200)'
                        : '1px solid var(--color-blue-200)',
                  }}
                >
                  {confirmModal.targetRole === 'TL' ? 'TE' : 'TL'}
                </span>
                <span style={{ color: 'var(--color-gray-400)', fontSize: '18px' }}>
                  →
                </span>
                <span
                  style={{
                    padding: '4px 12px',
                    borderRadius: '4px',
                    fontSize: '13px',
                    fontWeight: '600',
                    color:
                      confirmModal.targetRole === 'TL'
                        ? 'var(--color-blue-700)'
                        : 'var(--color-gray-600)',
                    background:
                      confirmModal.targetRole === 'TL'
                        ? 'var(--color-blue-50)'
                        : 'var(--color-gray-100)',
                    border:
                      confirmModal.targetRole === 'TL'
                        ? '1px solid var(--color-blue-200)'
                        : '1px solid var(--color-gray-200)',
                  }}
                >
                  {confirmModal.targetRole}
                </span>
              </div>
            </div>

            {/* 모달 버튼 */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '8px',
              }}
            >
              <button
                onClick={handleCloseModal}
                style={{
                  padding: '8px 20px',
                  borderRadius: '6px',
                  border: '1px solid var(--color-gray-300)',
                  background: 'white',
                  color: 'var(--color-gray-600)',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                }}
              >
                취소
              </button>
              <button
                onClick={handleConfirmRoleChange}
                disabled={tlLoading}
                style={{
                  padding: '8px 20px',
                  borderRadius: '6px',
                  border: 'none',
                  background: 'var(--color-primary)',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: tlLoading ? 'not-allowed' : 'pointer',
                  opacity: tlLoading ? 0.6 : 1,
                }}
              >
                {tlLoading ? '변경 중...' : '확인'}
              </button>
            </div>
          </div>
        </ModalOverlay>
      )}

      {/* V5.17: 조직원 프로필 카드 모달 */}
      <OrgMemberProfileModal
        isOpen={!!profileModalUserId}
        targetUserId={profileModalUserId}
        onClose={() => setProfileModalUserId(null)}
      />
    </div>
  );
}
